<script setup lang="ts">
import { computed, ref } from "vue";
import { locale } from "../../../locale";
import ToolTip from "../../ToolTip.vue";
import DeviceAllert from "../../icons/DeviceAllert.vue";
import { desiredMediaState, lockOnMicro, microDeviceError } from "../../../states/media/state";
import { mediaActions } from "../../../states/media/actions";
const btn = ref<HTMLElement | null>(null);
const changeMicro = () => {
  if (!microDeviceError.value && !lockOnMicro.value) {
    if(desiredMediaState.microEnabled) {
      mediaActions.microOff()
    } else {
      mediaActions.microOn()
    }
  }
};
const tooltip = computed(() => {
  if (microDeviceError.value) {
    return locale.deviceErrors.common;
  } else if (desiredMediaState.microEnabled) {
    return locale.voiceoff;
  } else {
    return locale.voiceon;
  }
});
</script>
<template>
  <button ref="btn" class="micro-button" @click="changeMicro" :class="{
    active: desiredMediaState.microEnabled,
    inactive: !!microDeviceError || lockOnMicro,
  }">
    <svg v-if="desiredMediaState.microEnabled" width="24" height="24" viewBox="0 0 24 24" fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6 10.0799V11.0399C6 12.8222 6.63214 14.5314 7.75736 15.7917C8.88258 17.0519 10.4087 17.7599 12 17.7599M12 17.7599C13.5913 17.7599 15.1174 17.0519 16.2426 15.7917C17.3679 14.5314 18 12.8222 18 11.0399V10.0799M12 17.7599V21.5999M12 21.5999H10.5M12 21.5999H13.5M12 2.3999C13.4202 2.3999 14.5714 3.68932 14.5714 5.2799V11.0399C14.5714 12.6305 13.4202 13.9199 12 13.9199C10.5798 13.9199 9.42857 12.6305 9.42857 11.0399V5.2799C9.42857 3.68932 10.5798 2.3999 12 2.3999Z"
        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.99961 10.0799V11.0399C5.99961 12.8222 6.47938 14.4597 7.67961 15.7199C8.87984 16.9801 10.3022 17.7599 11.9996 17.7599M11.9996 17.7599C13.697 17.7599 15.2994 16.8601 16.4996 15.5999C16.5444 15.5529 16.7472 15.6971 16.7905 15.6489M11.9996 17.7599V21.5999M17.9996 10.0799V11.0399C17.9996 11.6683 17.9807 12.0062 17.8196 12.5999M11.9996 21.5999H10.4996M11.9996 21.5999H13.7329M3.59961 2.3999L14.149 12.9927M20.3996 19.1999L16.7905 15.6489M9.39009 8.21419V11.0399C9.39009 12.6305 10.4848 13.9199 11.9996 13.9199C12.7968 13.9199 13.6479 13.5628 14.149 12.9927M14.149 12.9927L16.328 15.1806L16.7905 15.6489M14.5796 9.33324V5.2799C14.5796 3.6599 13.3663 2.3999 11.9996 2.3999C10.4996 2.3999 9.83961 3.5999 9.64835 4.05827"
        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    <ToolTip :placement="'top'" :reference="btn">{{
      tooltip
    }}</ToolTip>
    <div v-if="microDeviceError" class="error-allert">
      <DeviceAllert />
    </div>
  </button>
</template>
<style scoped>
.micro-button {
  width: 44px;
  height: 44px;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  background: var(--bottom-btn-off-bg-regular);
  color: var(--bottom-btn-off-content-regular);
  backdrop-filter: blur(2px);
  border-radius: 50px;
  border: none;
  cursor: pointer;
  position: relative;
}

.error-allert {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 16px;
  height: 16px;
}

.micro-button.inactive {
  cursor: default;
}

.micro-button:disabled {
  cursor: default;
}

.micro-button:hover:not(.inactive) {
  background-color: var(--bottom-btn-off-bg-hover);
  color: var(--bottom-btn-off-content-hover);
}

.micro-button:active:not(.inactive) {
  background-color: var(--bottom-btn-off-bg-active);
  color: var(--bottom-btn-off-content-active);
}

.micro-button:focus:not(.inactive) {
  outline: none;
}

.micro-button.active:hover:not(.inactive) {
  background-color: var(--bottom-btn-on-bg-hover);
  color: var(--bottom-btn-on-content-hover);
}

.micro-button.active:active:not(.inactive) {
  background-color: var(--bottom-btn-on-bg-active);
  color: var(--bottom-btn-on-content-active);
}

.micro-button.active:not(.inactive) {
  background-color: var(--bottom-btn-on-bg-regular);
  color: var(--bottom-btn-on-content-regular);
}
</style>
